import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Button,
  Text,
  Heading,
  Stack,
  Frame,
  TextContainer,
  Select,
  TextField,
  Icon,
  Tabs,
} from '@shopify/polaris';
import { CircleTickMajor, NoteMinor, ImageMajor } from '@shopify/polaris-icons';
import LandingNav from './components/LandingNav';
import templateData from '../../templateData';

// Add CSS styles for mobile responsiveness
const mobileStyles = `
  @media (max-width: 768px) {
    .desktop-template-features {
      display: none !important;
    }
    .mobile-template-features-slider {
      display: block !important;
    }
    .desktop-advanced-features {
      display: none !important;
    }
    .mobile-advanced-features-slider {
      display: block !important;
    }
  }
`;

const Templates = () => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState('product_description');
  const [textTemplate, setTextTemplate] = useState('seo_title');
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('medium');
  const [currentFeatureSlide, setCurrentFeatureSlide] = useState(0);
  const [currentAdvancedSlide, setCurrentAdvancedSlide] = useState(0);
  const [selectedExample, setSelectedExample] = useState('clothing');
  const [selectedAiModel, setSelectedAiModel] = useState('openai');
  const [showingPrompt, setShowingPrompt] = useState(true);
  const [inputValues, setInputValues] = useState({});
  const [inputCompleted, setInputCompleted] = useState({});
  
  // Add the mobile styles to the document head
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = mobileStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  
  const handleNavClick = (path) => {
    navigate(path);
  };
  
  const handleLoginClick = () => {
    navigate('/auth');
  };

  // Template demo data
  const templateTypes = [
    { id: 'text_templates', label: 'Text Templates' },
    { id: 'image_templates', label: 'Image Analyzing' },
    { id: 'tool_templates', label: 'Tool Execution' },
  ];

  const textTemplates = [
    { id: 'seo_title', name: 'SEO Title', icon: NoteMinor },
    { id: 'seo_description', name: 'SEO Description', icon: NoteMinor },
    { id: 'specifications', name: 'Specifications', icon: NoteMinor },
    // We'll expand these later
    // { id: 'product_description', name: 'Product Description', icon: NoteMinor },
    // { id: 'features_benefits', name: 'Features & Benefits', icon: NoteMinor },
  ];

  const imageTemplates = [
    { id: 'clothing', name: 'Clothing', icon: ImageMajor },
    { id: 'tech', name: 'Tech', icon: ImageMajor },
    { id: 'health', name: 'Health', icon: ImageMajor },
  ];

  const toneOptions = [
    {label: 'Professional', value: 'professional'},
    {label: 'Casual', value: 'casual'},
    {label: 'Enthusiastic', value: 'enthusiastic'},
    {label: 'Technical', value: 'technical'},
    {label: 'Luxury', value: 'luxury'},
  ];

  const lengthOptions = [
    {label: 'Short', value: 'short'},
    {label: 'Medium', value: 'medium'},
    {label: 'Long', value: 'long'},
  ];

  // Template output examples
  const templateOutputs = {
    product_description: {
      professional: 'The Premium Ergonomic Office Chair offers exceptional comfort and support for extended work sessions. Featuring adjustable lumbar support, breathable mesh backing, and customizable armrests, this chair promotes proper posture while reducing fatigue. Its durable construction supports up to 300 lbs, while the sleek design complements any modern workspace.',
      casual: "This awesome office chair is seriously comfortable! With adjustable back support that moves with you, breathable mesh to keep you cool, and super customizable armrests, you'll forget you're even sitting at work. It's built to last and looks pretty slick too - a perfect addition to any home office or workspace!",
      enthusiastic: "Discover the ULTIMATE solution for workplace comfort! Our INCREDIBLE Ergonomic Office Chair delivers AMAZING support with fully adjustable features that will TRANSFORM your workday experience! Feel the DIFFERENCE with our breathable mesh technology and ENJOY the perfect balance of style and functionality! Your back will THANK YOU!",
      technical: "Model EC-350 Ergonomic Chair features 3D-adjustable lumbar support (4.5\" vertical range), high-density mesh with 150,000 abrasion resistance rating, 7-point adjustment system, aircraft-grade aluminum frame, and 65mm dual-caster wheels with polyurethane coating. Meets BIFMA standards for stability and durability with 99.8% component recyclability.",
      luxury: "Indulge in the sophisticated comfort of our Signature Ergonomic Chair, an exquisite blend of innovative design and premium materials. Each chair is meticulously crafted with supple, full-grain leather and polished chrome accents, offering discerning professionals an elevated seating experience. The refined silhouette adds an air of distinction to executive environments."
    },
    features_benefits: {
      professional: "• Adjustable lumbar support maintains proper spine alignment\n• Breathable mesh back prevents overheating during extended use\n• 5-point base with smooth-rolling casters for stability and mobility\n• Multi-directional armrests reduce shoulder and neck strain\n• Height-adjustable pneumatic cylinder for optimal desk positioning",
      casual: "• Super comfy back support that adjusts to your body\n• Breathable mesh so you don't get all sweaty\n• Rolls around super smoothly on any floor\n• Armrests move however you want them\n• Easy height adjustment with just one lever",
      enthusiastic: "• INCREDIBLE adjustable support that PERFECTLY contours to your spine!\n• AMAZING breathable mesh that keeps you COOL all day long!\n• SMOOTH-GLIDING premium casters for EFFORTLESS movement!\n• FULLY CUSTOMIZABLE armrests for ULTIMATE comfort!\n• INSTANT height adjustment at your fingertips!",
      technical: "• Vertebral pressure distribution system with 4.5\\\" travel range\n• High-density polyethylene mesh (150K+ abrasion resistance)\n• 65mm dual-wheel casters with polyurethane coating (45kg load per caster)\n• 4D armrests with carbon-fiber reinforced adjustment mechanisms\n• Class 4 gas lift cylinder with 10-year operational rating",
      luxury: "• Precision-engineered lumbar system with hand-finished support panels\n• Premium breathable textile blend featuring natural fibers\n• Italian-designed base with whisper-quiet movement\n• Artisan-crafted armrests with soft-touch finish\n• Effortless height adjustment with signature polished lever"
    }
  };

  // Choose which template array to use based on selected tab
  const templates = selectedTab === 0 ? textTemplates : imageTemplates;
  // Initialize input values and completion status when template changes
  useEffect(() => {
    if (selectedTab === 0 && templateData.text[textTemplate]) {
      const initialValues = {};
      const initialCompleted = {};
      
      templateData.text[textTemplate].inputFields.forEach(field => {
        initialValues[field.id] = field.value;
        initialCompleted[field.id] = true; // Pre-filled values are considered completed
      });
      
      setInputValues(initialValues);
      setInputCompleted(initialCompleted);
    }
  }, [selectedTab, textTemplate]);

  // Check if all inputs are completed
  const areAllInputsCompleted = () => {
    if (selectedTab === 0 && templateData.text[textTemplate]) {
      return templateData.text[textTemplate].inputFields.every(field => 
        inputCompleted[field.id]
      );
    }
    return true;
  };

  // Get the appropriate content for the template preview based on current state
  const getTemplateContent = () => {
    // For text templates
    if (selectedTab === 0) {
      if (showingPrompt) {
        // Show the prompt with values inserted and highlighted field values
        let prompt = templateData.text[textTemplate]?.prompt || '';
        let formattedPrompt = prompt;
        
        // Add the input values section at the bottom of the prompt
        formattedPrompt += '\n\n---\n\nInput Values:';
        templateData.text[textTemplate]?.inputFields.forEach(field => {
          formattedPrompt += `\n${field.label}: ${inputValues[field.id] || field.value}`;
          // Also replace placeholders in the main prompt
          prompt = prompt.replace(`{${field.id}}`, inputValues[field.id] || field.value);
        });
        
        return formattedPrompt;
      } else {
        // Show the AI response
        return templateData.text[textTemplate]?.responses[selectedAiModel] || '';
      }
    }
    
    // For image templates, show either prompt or response based on state
    if (showingPrompt) {
      return templateData[selectedExample]?.prompt || '';
    } else {
      return templateData[selectedExample]?.responses[selectedAiModel] || '';
    }
  };

  const selectedTemplateData = getTemplateContent();

  return (
    <Frame>
      <Page>
        <LandingNav currentPath="/templates" onNavClick={handleNavClick} />
        
        {/* Hero Section */}
        <div style={{
          background: 'linear-gradient(135deg, #f5f9ff 0%, #eefbf4 100%)',
          padding: '40px 20px',
          borderRadius: '8px',
          marginBottom: '40px',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <Heading element="h1">
              <span style={{ fontSize: '2.2rem', fontWeight: '700', color: '#212B36' }}>
                Editify Template System
              </span>
            </Heading>
            <div style={{ margin: '20px 0', fontSize: '1.1rem', color: '#637381' }}>
              <p>Create professional Shopify product content with customizable AI templates that save you time and maintain brand consistency.</p>
            </div>
          </div>
        </div>

        {/* Template Demo */}
        <Card sectioned>
          <div style={{ 
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            marginBottom: '20px'
          }}>
            <div style={{ 
              display: 'flex',
              justifyContent: 'center',
              borderBottom: '1px solid #dfe3e8'
            }}>
              {templateTypes.map((type, index) => (
                <div 
                  key={type.id}
                  onClick={() => setSelectedTab(index)}
                  style={{
                    padding: '16px 24px',
                    cursor: 'pointer',
                    fontWeight: selectedTab === index ? '600' : '400',
                    borderBottom: selectedTab === index ? '3px solid #008060' : 'none',
                    marginBottom: selectedTab === index ? '-1px' : '0',
                    color: selectedTab === index ? '#008060' : '#637381'
                  }}
                >
                  {type.label}
                </div>
              ))}
            </div>
          </div>
          
          <div style={{ marginTop: '20px' }}>
            <Layout>
              <Layout.Section oneThird>
                <Card sectioned title="Template Settings">
                  <Stack vertical spacing="loose">
                    {selectedTab === 0 ? (
                      <>
                        <Select
                          label="Template Type"
                          options={templates.map(t => ({ label: t.name, value: t.id }))}
                          value={textTemplate}
                          onChange={setTextTemplate}
                        />
                        
                        <Select
                          label="Choose AI Model"
                          options={[
                            {label: 'OpenAI', value: 'openai'},
                            {label: 'DeepSeek', value: 'deepseek'},
                            {label: 'Gemini', value: 'gemini'},
                          ]}
                          value={selectedAiModel}
                          onChange={setSelectedAiModel}
                        />
                        
                        {/* Input fields for text template - simplified to just show field names and ticks */}
                        <div style={{ marginTop: '15px', border: '1px solid #E1E3E5', borderRadius: '4px', padding: '15px' }}>
                          <Text variant="headingSm" as="h3" fontWeight="semibold">Input Fields</Text>
                          {templateData.text[textTemplate]?.inputFields.map((field, index) => (
                            <div key={field.id} style={{ marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Text variant="bodyMd" as="span">{field.label}</Text>
                              <div style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: inputCompleted[field.id] ? '#008060' : 'white',
                                border: inputCompleted[field.id] ? 'none' : '1px solid #C9CCCF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {inputCompleted[field.id] && (
                                  <svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.5 6.59L10.09 1L11.5 2.41L4.5 9.41L0 4.91L1.41 3.5L4.5 6.59Z" fill="white"/>
                                  </svg>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <Select
                          label="Choose Example"
                          options={templates.map(t => ({ label: t.name, value: t.id }))}
                          value={selectedExample}
                          onChange={setSelectedExample}
                        />
                        
                        <Select
                          label="Choose AI Model"
                          options={[
                            {label: 'OpenAI', value: 'openai'},
                            {label: 'Gemini', value: 'gemini'},
                          ]}
                          value={selectedAiModel}
                          onChange={setSelectedAiModel}
                        />
                        
                        <div style={{ marginTop: '20px' }}>
                          <div style={{ 
                            borderRadius: '4px', 
                            padding: '20px',
                            textAlign: 'center'
                          }}>
                            <img 
                              src={`/images/templates/${selectedExample.charAt(0).toUpperCase() + selectedExample.slice(1)}.jpg`} 
                              alt={`${selectedExample} example`}
                              style={{ 
                                maxWidth: '100%', 
                                height: 'auto', 
                                borderRadius: '8px',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                              }}
                            />
                          </div>
                        </div>
                      </>
                    )}
                    
                    <div style={{ marginTop: '10px' }}>
                      {!showingPrompt ? (
                        <Button 
                          fullWidth 
                          onClick={() => setShowingPrompt(true)}
                        >
                          Back to Prompt
                        </Button>
                      ) : (
                        <Button 
                          primary 
                          fullWidth 
                          disabled={selectedTab === 0 ? !areAllInputsCompleted() : !selectedTemplate}
                          onClick={() => {
                            if (showingPrompt) {
                              setShowingPrompt(false);
                            }
                          }}
                        >
                          Generate Content
                        </Button>
                      )}
                    </div>
                  </Stack>
                </Card>
              </Layout.Section>
              
              <Layout.Section>
                <Card sectioned title={selectedTab === 0 || !showingPrompt ? "Template Output Preview" : "Prompt"}>
                  <TextContainer>
                    <div style={{
                      padding: '15px',
                      borderRadius: '4px',
                      backgroundColor: '#f9fafb',
                      minHeight: '250px',
                      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      whiteSpace: 'pre-line',
                      lineHeight: '1.5'
                    }}>
                      {selectedTemplateData}
                    </div>
                  </TextContainer>
                </Card>
              </Layout.Section>
            </Layout>
          </div>
        </Card>

        {/* Template Features */}
        <div style={{ marginTop: '40px' }}>
          <Heading>Why Use Our Template System?</Heading>
          
          {/* Desktop view - template features */}
          <div className="desktop-template-features" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '20px',
            marginTop: '20px'
          }}>
            {[
              {
                icon: '⚡',
                title: 'Save Time',
                description: 'Generate quality content in seconds instead of hours of manual writing'
              },
              {
                icon: '🔄',
                title: 'Reuse Templates',
                description: 'Save your custom templates for future use across your product catalog'
              },
              {
                icon: '📱',
                title: 'Multi-field Support',
                description: 'Generate content for descriptions, features, SEO fields, and more'
              },
            ].map((feature, index) => (
              <div key={index} style={{ height: '100%' }}>
                <Card sectioned>
                  <Stack vertical alignment="center">
                    <div style={{ fontSize: '32px', marginBottom: '15px', color: feature.icon === '⚡' ? '#FF9900' : '#008060' }}>{feature.icon}</div>
                    <Text variant="headingMd" as="h3">{feature.title}</Text>
                    <div style={{ margin: '15px 0' }}>
                      <Text variant="bodyMd" as="p" alignment="center">{feature.description}</Text>
                    </div>
                  </Stack>
                </Card>
              </div>
            ))}
          </div>
          
          {/* Mobile slider for template features */}
          <div className="mobile-template-features-slider" style={{
            display: 'none',
            position: 'relative',
            maxWidth: '300px',
            margin: '30px auto 0'
          }}>
            <div style={{
              overflow: 'hidden',
              position: 'relative',
              borderRadius: '10px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div className="slider-container" style={{
                display: 'flex',
                transition: 'transform 0.3s ease-in-out',
                transform: `translateX(-${currentFeatureSlide * 100}%)`
              }}>
                {/* Feature 1 - Save Time */}
                <div style={{
                  minWidth: '100%',
                  background: '#ffffff',
                  padding: '35px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '16px', color: '#FF9900' }}>⚡</div>
                  <Text variant="headingMd" as="h3">Save Time</Text>
                  <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                    Generate quality content in seconds instead of hours of manual writing
                  </Text>
                </div>
                
                {/* Feature 2 - Reuse Templates */}
                <div style={{
                  minWidth: '100%',
                  background: '#ffffff',
                  padding: '35px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '16px', color: '#008060' }}>🔄</div>
                  <Text variant="headingMd" as="h3">Reuse Templates</Text>
                  <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                    Save your custom templates for future use across your product catalog
                  </Text>
                </div>
                
                {/* Feature 3 - Multi-field Support */}
                <div style={{
                  minWidth: '100%',
                  background: '#ffffff',
                  padding: '35px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '16px', color: '#008060' }}>📱</div>
                  <Text variant="headingMd" as="h3">Multi-field Support</Text>
                  <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                    Generate content for descriptions, features, SEO fields, and more
                  </Text>
                </div>
              </div>
            </div>
            
            {/* Slider dots */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '15px',
              gap: '8px'
            }}>
              {[0, 1, 2].map((index) => (
                <button 
                  key={index}
                  onClick={() => setCurrentFeatureSlide(index)}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: currentFeatureSlide === index ? '#008060' : '#e1e1e1',
                    cursor: 'pointer',
                    padding: 0
                  }}
                  aria-label={`Go to feature slide ${index + 1}`}
                />
              ))}
            </div>
            
            {/* Left/Right navigation arrows */}
            <button 
              onClick={() => setCurrentFeatureSlide(prev => (prev === 0 ? 2 : prev - 1))}
              style={{
                position: 'absolute',
                left: '-15px',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'white',
                border: '1px solid #e1e1e1',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
              }}
              aria-label="Previous feature slide"
            >
              ‹
            </button>
            <button 
              onClick={() => setCurrentFeatureSlide(prev => (prev === 2 ? 0 : prev + 1))}
              style={{
                position: 'absolute',
                right: '-15px',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'white',
                border: '1px solid #e1e1e1',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
              }}
              aria-label="Next feature slide"
            >
              ›
            </button>
          </div>
        </div>

        {/* Advanced Template Tools Section */}
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <Heading>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <span style={{ fontSize: '28px' }}>✨</span>
              <span>Advanced Template Tools</span>
            </span>
          </Heading>
          
          <Text as="p" style={{ margin: '15px auto 30px', maxWidth: '800px', fontSize: '1.1rem', color: '#637381' }}>
            Take your product content creation to the next level with powerful customization and automation tools.
          </Text>
          
          {/* Advanced features grid - desktop view */}
          <div className="desktop-advanced-features" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '25px', 
            marginTop: '30px'
          }}>
            {[
              {
                icon: '✅',
                title: 'Duplicate Templates',
                description: 'Easily clone templates for A/B testing and seasonal changes.',
                note: '🔁 Save time by tweaking existing work.'
              },
              {
                icon: '⚙️',
                title: 'Create Custom Workflows',
                description: 'Chain templates together in a specific order for automation.',
                note: '📈 Build Title → Description → Tags workflows.'
              },
              {
                icon: '🧠',
                title: 'Per-Template AI Engine Selection',
                description: 'Choose the right AI engine for each specific template.',
                note: '🎯 Get precision in every step of your content pipeline.'
              }
            ].map((feature, index) => (
              <div key={index} style={{ height: '100%' }}>
                <Card sectioned>
                  <Stack vertical alignment="center">
                    <div style={{ fontSize: '32px', marginBottom: '15px', color: '#008060' }}>{feature.icon}</div>
                    <Text variant="headingMd" as="h3">{feature.title}</Text>
                    <div style={{ margin: '15px 0' }}>
                      <Text variant="bodyMd" as="p" alignment="center">{feature.description}</Text>
                    </div>
                    <Text variant="bodySm" as="p" alignment="center" color="subdued">{feature.note}</Text>
                  </Stack>
                </Card>
              </div>
            ))}
          </div>
          
          {/* Mobile slider for advanced template tools */}
          <div className="mobile-advanced-features-slider" style={{
            display: 'none',
            position: 'relative',
            maxWidth: '300px',
            margin: '30px auto 0'
          }}>
            <div style={{
              overflow: 'hidden',
              position: 'relative',
              borderRadius: '10px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div className="slider-container" style={{
                display: 'flex',
                transition: 'transform 0.3s ease-in-out',
                transform: `translateX(-${currentAdvancedSlide * 100}%)`
              }}>
                {/* Advanced Tool 1 - Duplicate Templates */}
                <div style={{
                  minWidth: '100%',
                  background: '#ffffff',
                  padding: '35px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '16px', color: '#008060' }}>✅</div>
                  <Text variant="headingMd" as="h3">Duplicate Templates</Text>
                  <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                    Easily clone templates for A/B testing and seasonal changes.
                  </Text>
                  <Text variant="bodySm" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                    🔄 Save time by tweaking existing work.
                  </Text>
                </div>
                
                {/* Advanced Tool 2 - Create Custom Workflows */}
                <div style={{
                  minWidth: '100%',
                  background: '#ffffff',
                  padding: '35px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '16px', color: '#008060' }}>⚙️</div>
                  <Text variant="headingMd" as="h3">Create Custom Workflows</Text>
                  <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                    Chain templates together in a specific order for automation.
                  </Text>
                  <Text variant="bodySm" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                    📈 Build Title → Description → Tags workflows.
                  </Text>
                </div>
                
                {/* Advanced Tool 3 - Per-Template AI Engine Selection */}
                <div style={{
                  minWidth: '100%',
                  background: '#ffffff',
                  padding: '35px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '16px', color: '#008060' }}>🧠</div>
                  <Text variant="headingMd" as="h3">Per-Template AI Engine Selection</Text>
                  <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                    Choose the right AI engine for each specific template.
                  </Text>
                  <Text variant="bodySm" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                    🎨 Get precision in every step of your content pipeline.
                  </Text>
                </div>
              </div>
            </div>
            
            {/* Slider dots */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '15px',
              gap: '8px'
            }}>
              {[0, 1, 2].map((index) => (
                <button 
                  key={index}
                  onClick={() => setCurrentAdvancedSlide(index)}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: currentAdvancedSlide === index ? '#008060' : '#e1e1e1',
                    cursor: 'pointer',
                    padding: 0
                  }}
                  aria-label={`Go to advanced tool slide ${index + 1}`}
                />
              ))}
            </div>
            
            {/* Left/Right navigation arrows */}
            <button 
              onClick={() => setCurrentAdvancedSlide(prev => (prev === 0 ? 2 : prev - 1))}
              style={{
                position: 'absolute',
                left: '-15px',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'white',
                border: '1px solid #e1e1e1',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
              }}
              aria-label="Previous advanced tool slide"
            >
              ‹
            </button>
            <button 
              onClick={() => setCurrentAdvancedSlide(prev => (prev === 2 ? 0 : prev + 1))}
              style={{
                position: 'absolute',
                right: '-15px',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'white',
                border: '1px solid #e1e1e1',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
              }}
              aria-label="Next advanced tool slide"
            >
              ›
            </button>
          </div>
        </div>

        {/* CTA Section */}
        <div style={{ 
          marginTop: '60px', 
          padding: '40px 20px', 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #f9f9ff 0%, #f0f9ff 100%)',
          borderRadius: '8px'
        }}>
          <Heading>Create Professional Product Content in Seconds</Heading>
          <Text as="p" style={{ margin: '15px auto 30px', maxWidth: '650px' }}>
            Stop writing product descriptions from scratch. Start using AI-powered templates.
          </Text>
          <Button size="large" primary onClick={handleLoginClick}>
            Try Templates Now
          </Button>
        </div>
      </Page>
    </Frame>
  );
};

export default Templates;
